/**
 * Dashboard Progress Service
 * 
 * Aggregates drill progress data from multiple sources:
 * - drill_section_progress (section completions)
 * - user_learning_stages (learning stages)
 * - drills table (drill metadata)
 */

import { supabase } from '@/lib/supabaseClient';
import { buildDrillTimeline } from './drill-timeline';

// ============================================================================
// Types
// ============================================================================

export type DrillStatus = 'not_started' | 'in_progress' | 'complete' | 'mastered';

export interface DrillProgress {
  drill_id: string;
  title: string;
  module_id: string | null;
  sections_completed: number;
  total_sections: number;
  completion_percentage: number;
  learning_stage: 'cognitive' | 'associative' | 'autonomous' | null;
  last_attempt_date: string | null;
  status: DrillStatus;
}

export interface ModuleProgress {
  module_id: string;
  module_name: string;
  drills: DrillProgress[];
  completion_percentage: number;
}

export interface ResumeDrill {
  drill_id: string;
  title: string;
  last_section: string;
  progress: number;
  last_attempt_date: string;
}

export interface OverallStats {
  total_drills: number;
  completed_drills: number;
  in_progress_drills: number;
  overall_completion_percentage: number;
}

export interface DashboardProgressSummary {
  user_id: string;
  modules: ModuleProgress[];
  resume_drill: ResumeDrill | null;
  overall_stats: OverallStats;
}

// ============================================================================
// Main Service Function
// ============================================================================

/**
 * Get comprehensive progress summary for dashboard
 */
export async function getProgressSummary(userId: string): Promise<DashboardProgressSummary> {
  // Fetch all drills
  const { data: allDrills, error: drillsError } = await supabase
    .from('drills')
    .select('id, title, module_id, video_sections_json')
    .order('module_id', { ascending: true })
    .order('title', { ascending: true });

  if (drillsError) {
    throw new Error(`Failed to fetch drills: ${drillsError.message}`);
  }

  if (!allDrills || allDrills.length === 0) {
    return createEmptySummary(userId);
  }

  // Fetch user's section progress
  const { data: sectionProgress, error: progressError } = await supabase
    .from('drill_section_progress')
    .select('drill_id, section_id, completed_at')
    .eq('user_id', userId);

  if (progressError) {
    console.error('Failed to fetch section progress:', progressError);
  }

  // Fetch user's learning stages
  const { data: learningStages, error: stagesError } = await supabase
    .from('user_learning_stages')
    .select('drill_id, learning_stage, assessment_date')
    .eq('user_id', userId);

  if (stagesError) {
    console.error('Failed to fetch learning stages:', stagesError);
  }

  // Build progress for each drill
  const drillProgressList: DrillProgress[] = [];
  
  for (const drill of allDrills) {
    const drillId = drill.id.toString();
    
    // Count sections for this drill
    const drillSections = (sectionProgress || []).filter(sp => sp.drill_id === drillId);
    const totalSections = getTotalSections(drill.video_sections_json);
    const completedSections = drillSections.length;
    
    // Get learning stage
    const stage = (learningStages || []).find(ls => ls.drill_id === drillId);
    
    // Get last attempt date
    const lastAttempt = drillSections.length > 0
      ? drillSections.sort((a, b) => 
          new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime()
        )[0].completed_at
      : null;

    const completionPercentage = calculateProgress(completedSections, totalSections);
    const status = getStatusFromProgress(completionPercentage, stage?.learning_stage || null);

    drillProgressList.push({
      drill_id: drillId,
      title: drill.title || 'Untitled Drill',
      module_id: drill.module_id,
      sections_completed: completedSections,
      total_sections: totalSections,
      completion_percentage: completionPercentage,
      learning_stage: stage?.learning_stage || null,
      last_attempt_date: lastAttempt,
      status,
    });
  }

  // Group by module
  const modules = groupByModule(drillProgressList);

  // Find resume drill (most recent incomplete)
  const resumeDrill = findResumeDrill(drillProgressList, sectionProgress || []);

  // Calculate overall stats
  const overall_stats = calculateOverallStats(drillProgressList);

  return {
    user_id: userId,
    modules,
    resume_drill: resumeDrill,
    overall_stats,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

function getTotalSections(videoSectionsJson: any): number {
  if (!videoSectionsJson) return 1; // default to 1 section
  
  try {
    const sections = Array.isArray(videoSectionsJson) 
      ? videoSectionsJson 
      : JSON.parse(videoSectionsJson);
    return Array.isArray(sections) ? sections.length : 1;
  } catch {
    return 1;
  }
}

function calculateProgress(completed: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}

function getStatusFromProgress(
  percentage: number,
  stage: string | null
): DrillStatus {
  if (percentage === 0) return 'not_started';
  if (percentage === 100 && stage === 'autonomous') return 'mastered';
  if (percentage === 100) return 'complete';
  return 'in_progress';
}

function groupByModule(drills: DrillProgress[]): ModuleProgress[] {
  const moduleMap = new Map<string, DrillProgress[]>();

  // Group drills by module
  for (const drill of drills) {
    const moduleId = drill.module_id || 'other';
    if (!moduleMap.has(moduleId)) {
      moduleMap.set(moduleId, []);
    }
    moduleMap.get(moduleId)!.push(drill);
  }

  // Convert to ModuleProgress array
  const modules: ModuleProgress[] = [];
  
  for (const [moduleId, moduleDrills] of moduleMap.entries()) {
    const totalCompletion = moduleDrills.reduce(
      (sum, drill) => sum + drill.completion_percentage,
      0
    );
    const avgCompletion = moduleDrills.length > 0
      ? Math.round(totalCompletion / moduleDrills.length)
      : 0;

    modules.push({
      module_id: moduleId,
      module_name: getModuleName(moduleId),
      drills: moduleDrills,
      completion_percentage: avgCompletion,
    });
  }

  return modules.sort((a, b) => a.module_id.localeCompare(b.module_id));
}

function getModuleName(moduleId: string): string {
  const moduleNames: Record<string, string> = {
    'M01': 'Module 1: Grip & Stance',
    'M02': 'Module 2: Rotation & Tempo',
    'M03': 'Module 3: Advanced Fundamentals',
    'other': 'Other Drills',
  };
  return moduleNames[moduleId] || `Module ${moduleId}`;
}

function findResumeDrill(
  drills: DrillProgress[],
  sectionProgress: any[]
): ResumeDrill | null {
  // Find in-progress drills
  const inProgress = drills
    .filter(d => d.status === 'in_progress' && d.last_attempt_date)
    .sort((a, b) => {
      const dateA = a.last_attempt_date ? new Date(a.last_attempt_date).getTime() : 0;
      const dateB = b.last_attempt_date ? new Date(b.last_attempt_date).getTime() : 0;
      return dateB - dateA; // most recent first
    });

  if (inProgress.length === 0) return null;

  const mostRecent = inProgress[0];
  
  // Find last completed section
  const drillSections = sectionProgress
    .filter(sp => sp.drill_id === mostRecent.drill_id)
    .sort((a, b) => 
      new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime()
    );

  const lastSection = drillSections.length > 0 ? drillSections[0].section_id : 'unknown';

  return {
    drill_id: mostRecent.drill_id,
    title: mostRecent.title,
    last_section: lastSection,
    progress: mostRecent.completion_percentage,
    last_attempt_date: mostRecent.last_attempt_date!,
  };
}

function calculateOverallStats(drills: DrillProgress[]): OverallStats {
  const total = drills.length;
  const completed = drills.filter(d => 
    d.status === 'complete' || d.status === 'mastered'
  ).length;
  const inProgress = drills.filter(d => d.status === 'in_progress').length;

  const totalCompletion = drills.reduce(
    (sum, drill) => sum + drill.completion_percentage,
    0
  );
  const overallPercentage = total > 0 ? Math.round(totalCompletion / total) : 0;

  return {
    total_drills: total,
    completed_drills: completed,
    in_progress_drills: inProgress,
    overall_completion_percentage: overallPercentage,
  };
}

function createEmptySummary(userId: string): DashboardProgressSummary {
  return {
    user_id: userId,
    modules: [],
    resume_drill: null,
    overall_stats: {
      total_drills: 0,
      completed_drills: 0,
      in_progress_drills: 0,
      overall_completion_percentage: 0,
    },
  };
}

